
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { fetchPublishedArticles, Article } from '../../../utils/articleApi';
import ArticleCategoryBadges from '../../../components/feature/ArticleCategoryBadges';
import AuthorAvatar from '../../../components/feature/AuthorAvatar';

export default function FeaturedArticles() {
  const location = useLocation();
  const [featuredArticles, setFeaturedArticles] = useState<Article[]>([]);

  useEffect(() => {
    const loadArticles = async () => {
      const articles = await fetchPublishedArticles();
      const published = articles.filter((article) => article.status === '公開');
      // すべての公開記事を表示（最大7件）
      setFeaturedArticles(published.slice(0, 7));
    };
    loadArticles();
  }, []);

  return (
    <section className="py-8 sm:py-12 md:py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-10 md:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#111827] mb-3 sm:mb-4">
            注目の旅行記事
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-[#6B7280] max-w-2xl mx-auto px-4">
            編集部が厳選した、今読むべき旅行記事をお届けします
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {featuredArticles.map((article, index) => (
            <Link 
              key={article.id}
              to={`/articles/${article.id}`}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
            >
              <div className="aspect-video overflow-hidden">
                <img 
                  src={article.image}
                  alt={article.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-2 sm:mb-3 gap-2">
                  <ArticleCategoryBadges article={article} />
                  {index === 0 && (
                    <span className="bg-red-500 text-white px-2 py-1 rounded text-xs font-bold">
                      NEW
                    </span>
                  )}
                </div>
                
                <h3 className="text-lg sm:text-xl font-bold text-[#111827] mb-2 sm:mb-3 group-hover:text-[#14B8A6] transition-colors line-clamp-2">
                  {article.title}
                </h3>
                
                <p className="text-sm sm:text-base text-[#6B7280] mb-3 sm:mb-4 line-clamp-3">
                  {article.excerpt}
                </p>
                
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 text-xs sm:text-sm text-[#6B7280]">
                  <div className="flex items-center space-x-2">
                    <AuthorAvatar size="responsive" />
                    <span className="truncate">{article.author}</span>
                  </div>
                  <div className="flex items-center space-x-2 sm:space-x-4">
                    <span className="flex items-center space-x-1">
                      <i className="ri-time-line"></i>
                      <span className="whitespace-nowrap">{article.readTime}</span>
                    </span>
                    <span className="whitespace-nowrap">{article.publishDate}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-8 sm:mt-10 md:mt-12">
          <Link
            to="/articles#categories"
            onClick={(e) => {
              if (location.pathname !== '/articles') return;
              e.preventDefault();
              document.getElementById('categories')?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
              window.history.replaceState(null, '', '/articles#categories');
            }}
            className="inline-flex items-center space-x-2 bg-[#14B8A6] text-white px-6 sm:px-8 py-2 sm:py-3 rounded-lg hover:bg-[#0F9488] transition-colors font-medium text-sm sm:text-base"
          >
            <span>すべての旅行記事を見る</span>
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
